import { creatorbase } from '@round/api';
import { Avatar, Button, Dialog, lucideIcons, Skeleton } from '@round/creatorbase-ui';
import {
    CopyButton,
    InstagramPostEmbed,
    TikTokPostEmbed,
    YouTubePostEmbed,
} from '@round/creatorbase-helpers/components';
import { formatDecimalToPercentageString } from '@round/utils';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';
import {
    formatPostStatsCellValue,
    getInstagramEngagementRate,
    getInstagramTotalEngagement,
    getTiktokEngagementRate,
    getTikTokTotalEngagement,
    getYoutubeEngagementRatePercentageString,
    getYoutubeTotalEngagement,
} from 'Modules/Plans/Posts/helpers';
import cn from 'classnames';
import moment from 'moment';
import { ReactNode, useRef } from 'react';
import styles from './PostStatsModal.module.css';

const { SquareArrowOutUpRight: ExternalLinkIcon } = lucideIcons;

const EMPTY_STAT_VALUE = '-';
const EMPTY_ACCOUNT_NAME = '-';
const UPLOAD_DATE_FORMAT = 'DD MMM YYYY';

/** The account that posted, loaded by the caller the same way its posts table loads the Account column. */
export type PostStatsModalAccount = {
    /** Display name. Left empty, the handle takes its place. */
    name?: string | null;
    /** Without the leading @. YouTube channels have none. */
    handle?: string | null;
    image?: string | null;
    /** Opened from the handle, or from the name when there is no handle. */
    profileUrl?: string | null;
    isLoading?: boolean;
};

type Props = {
    post: creatorbase.PublicPost;
    /** Null when the post has no account, or its account couldn't be found. */
    account: PostStatsModalAccount | null;
    isOpen: boolean;
    onClose: () => void;
    className?: string;
};

const PostStatsModal = ({ post, account, isOpen, onClose, className }: Props) => {
    const detailsRef = useRef<HTMLDivElement>(null);

    // On narrow screens the embed and the details share one scrolling column, so a plain focus would open the dialog
    // scrolled past the embed and the close button. Returning false stops the dialog from moving focus again.
    const focusDetails = () => {
        detailsRef.current?.focus({ preventScroll: true });
        return false;
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <Dialog.Content
                className={cn(styles.content, className)}
                aria-label="Post stats"
                // The first tabbable element would otherwise take focus on open and show its focus ring.
                initialFocus={focusDetails}
            >
                <Dialog.Body className={styles.body}>
                    <PostEmbed post={post} />

                    <div ref={detailsRef} tabIndex={-1} className={styles.details}>
                        <PostAccount account={account} uploadDate={post.upload_date} />

                        <div className={styles.actions}>
                            <Button asChild variant="outline" size="xs" className={styles.viewPost}>
                                <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLinkIcon />
                                    View post
                                </a>
                            </Button>

                            <CopyButton
                                value={post.post_url}
                                variant="outline"
                                size="icon-sm"
                                className={styles.copyLink}
                                tooltipLabel="Copy post link"
                                aria-label="Copy post link"
                            />
                        </div>

                        <dl className={styles.stats}>
                            {getPostStats(post).map((stat) => (
                                <div key={stat.label} className={styles.stat}>
                                    <dt className={styles.label}>
                                        {stat.tooltip ? (
                                            <TooltipHeader header={stat.label} tooltipBody={stat.tooltip} />
                                        ) : (
                                            stat.label
                                        )}
                                    </dt>
                                    <dd className={styles.value}>{stat.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </Dialog.Body>
            </Dialog.Content>
        </Dialog>
    );
};

export default PostStatsModal;

type PostAccountProps = {
    account: PostStatsModalAccount | null;
    uploadDate: string | null;
};

const PostAccount = ({ account, uploadDate }: PostAccountProps) => {
    const handle = account?.handle ? `@${account.handle}` : null;
    // A missing display name hands its line to the handle rather than repeating it underneath.
    const name = account?.name || handle;
    const secondaryHandle = account?.name ? handle : null;
    const profileUrl = account?.profileUrl;

    return (
        <div className={styles.account}>
            {account?.isLoading ? (
                <Skeleton className={styles.avatarSkeleton} />
            ) : (
                <Avatar size="lg" className={styles.avatar}>
                    <Avatar.Image className={styles.avatarImage} src={account?.image ?? undefined} alt="" />
                    <Avatar.Fallback className={styles.avatarFallback}>
                        {name ? getInitials(name) : null}
                    </Avatar.Fallback>
                </Avatar>
            )}

            <div className={styles.identity}>
                {account?.isLoading ? (
                    <>
                        <Skeleton className={cn(styles.name, styles.nameSkeleton)} />
                        <Skeleton className={cn(styles.secondary, styles.handleSkeleton)} />
                    </>
                ) : (
                    <>
                        {name ? (
                            <ProfileLink className={styles.name} url={secondaryHandle ? null : profileUrl} title={name}>
                                {name}
                            </ProfileLink>
                        ) : (
                            <span className={styles.name}>{EMPTY_ACCOUNT_NAME}</span>
                        )}

                        {secondaryHandle && (
                            <ProfileLink className={styles.secondary} url={profileUrl} title={secondaryHandle}>
                                {secondaryHandle}
                            </ProfileLink>
                        )}
                    </>
                )}

                {uploadDate && (
                    <time className={styles.secondary} dateTime={uploadDate}>
                        {moment(uploadDate).format(UPLOAD_DATE_FORMAT)}
                    </time>
                )}
            </div>
        </div>
    );
};

type ProfileLinkProps = {
    url: string | null | undefined;
    className: string;
    title: string;
    children: ReactNode;
};

const ProfileLink = ({ url, className, title, children }: ProfileLinkProps) => {
    if (!url) {
        return (
            <span className={className} title={title}>
                {children}
            </span>
        );
    }

    return (
        <a
            className={cn(className, styles.profileLink)}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
        >
            {children}
        </a>
    );
};

const MAX_INITIALS = 2;

/** Splits on the separators names and handles use, skipping parts like emoji that don't start with a letter or digit. */
const getInitials = (name: string) =>
    name
        .replace(/^@/, '')
        .split(/[\s._-]+/)
        .filter((part) => /^[\p{L}\p{N}]/u.test(part))
        .slice(0, MAX_INITIALS)
        .map((part) => Array.from(part)[0].toUpperCase())
        .join('');

const PostEmbed = ({ post }: { post: creatorbase.PublicPost }) => {
    switch (post.platform) {
        case 'tiktok':
            return <TikTokPostEmbed url={post.post_url} className={styles.embed} />;
        case 'instagram':
            return <InstagramPostEmbed url={post.post_url} className={cn(styles.embed, styles.instagramEmbed)} />;
        case 'youtube':
            return <YouTubePostEmbed url={post.post_url} className={styles.embed} />;
    }
};

type PostStat = {
    label: string;
    value: string;
    tooltip?: string;
};

const formatEngagementRate = (engagementRate: number | null) => {
    if (engagementRate === null) {
        return EMPTY_STAT_VALUE;
    }

    return formatDecimalToPercentageString(engagementRate);
};

// Mirrors the stat columns of the platform's posts table, in the same order.
const getPostStats = (post: creatorbase.PublicPost): PostStat[] => {
    switch (post.platform) {
        case 'tiktok':
            return [
                { label: 'Views', value: formatPostStatsCellValue(post.view_count) },
                { label: 'Total engagements', value: formatPostStatsCellValue(getTikTokTotalEngagement(post)) },
                {
                    label: 'Engagement rate',
                    tooltip: tooltipCopy.engagementRate,
                    value: formatEngagementRate(getTiktokEngagementRate(post)),
                },
                { label: 'Likes', value: formatPostStatsCellValue(post.tiktok_details?.like_count) },
                { label: 'Shares', value: formatPostStatsCellValue(post.tiktok_details?.share_count) },
                { label: 'Comments', value: formatPostStatsCellValue(post.tiktok_details?.comment_count) },
                { label: 'Saves', value: formatPostStatsCellValue(post.tiktok_details?.save_count) },
            ];
        case 'instagram':
            return [
                { label: 'Views', value: formatPostStatsCellValue(post.view_count) },
                { label: 'Story views', value: formatPostStatsCellValue(post.instagram_details?.story_view_count) },
                {
                    label: 'Total engagements',
                    value: formatPostStatsCellValue(getInstagramTotalEngagement(post.instagram_details)),
                },
                {
                    label: 'Engagement rate',
                    tooltip: tooltipCopy.engagementRate,
                    value: formatEngagementRate(getInstagramEngagementRate(post)),
                },
                { label: 'Likes', value: formatPostStatsCellValue(post.instagram_details?.like_count) },
                { label: 'Comments', value: formatPostStatsCellValue(post.instagram_details?.comment_count) },
            ];
        case 'youtube':
            return [
                { label: 'Views', value: formatPostStatsCellValue(post.view_count) },
                { label: 'Total engagements', value: formatPostStatsCellValue(getYoutubeTotalEngagement(post)) },
                {
                    label: 'Engagement rate',
                    tooltip: tooltipCopy.engagementRate,
                    value: getYoutubeEngagementRatePercentageString(post),
                },
                { label: 'Likes', value: formatPostStatsCellValue(post.youtube_details?.like_count) },
                { label: 'Comments', value: formatPostStatsCellValue(post.youtube_details?.comment_count) },
            ];
    }
};
